<script setup lang="ts">
import { computed, toRef, watch, type ComputedRef } from 'vue';
import { useI18n } from 'vue-i18n';

import LocalizedEditor from '@/components/base/LocalizedEditor.vue';
import LocalizedSelect from '@/components/base/LocalizedSelect.vue';
import ImageField from '@/components/ImageField.vue';
import LocalizedInput from '@/components/LocalizedInput.vue';
import StandardToggle from '../StandardToggle.vue';

import { formSaveImageGuard } from '@/helpers/form.helper';
import type { Locales, ProjectModuleDto, ProjectModuleTextImageConfigDto, ThemeHost } from '@/services/types/api.types';
import type { SavingOptions } from '@/services/types/file-uploader.types';
import { useBlocksStore } from '@/stores';
import { useField, useIsFormDirty } from 'vee-validate';
import { useConfigurationStore } from '@/stores/configurations.store';
import type { FileInfo } from '@/services/types/file.types';
import { useTheme } from '@/composables/useTheme';
import { ModuleTypeEnum } from '@/types/modules';

const blocksStore = useBlocksStore();

// CONST
const { t } = useI18n();
const alignmentOptions = computed(() => blocksStore.getImageAlignmentOptions());
const isProjectTOCEnabled = useConfigurationStore().isFeatureEnabled('projectTOC');
const file = computed<FileInfo | null>(() => (moduleConfig.value.image as FileInfo) ?? null);

// PROPS
const props = defineProps<{
  languageEnabled: Locales[];
  editLocale: Locales;
  module: ProjectModuleDto;
  themeKey: string;
  themeHost: ThemeHost;
}>();

const emit = defineEmits<{
  (e: 'saveImage', options: SavingOptions): void;
  (e: 'deleteImage'): void;
}>();

// COMPUTED
const moduleConfig: ComputedRef<ProjectModuleTextImageConfigDto> = computed(
  () => (props.module.config as ProjectModuleTextImageConfigDto) || ({} as ProjectModuleTextImageConfigDto),
);

const { value: hasImageValue } = useField<boolean>('config.hasImage');
const { value: image } = useField<FileInfo | null>('config.image');
const { value: imageAlignment } = useField<number>('config.imageAlignment');

const themeConfiguration = useTheme(toRef(props, 'themeHost'), toRef(props, 'themeKey'));
const layout = computed(() => (imageAlignment.value === 3 ? 2 : 1));
const cropping = themeConfiguration.getCroppingInfo(ModuleTypeEnum.Text, layout);

const isDirty = useIsFormDirty();

// FUNCTIONS
const handleSaveImage = async (options: SavingOptions) => {
  emit('saveImage', options);
};

function removeImage() {
  emit('deleteImage');
}

const handleClearImage = async () => {
  formSaveImageGuard(false, removeImage);
};

watch(hasImageValue, (newValue) => {
  if (!newValue) image.value = null;
});
</script>
<template>
  <div>
    <localized-input
      name="title"
      :label="t('global.label.title')"
      :editLocale="editLocale"
      outlined
      :language_enabled="props.languageEnabled"
    />
    <q-field borderless :label="t('global.label.content')" stack-label>
      <template v-slot:control>
        <localized-editor
          name="config.content"
          :label="t('global.label.content')"
          class="q-mt-sm full-width"
          :editLocale="props.editLocale"
          :language_enabled="languageEnabled"
        />
      </template>
    </q-field>
    <standard-toggle name="config.highlight" :label="t('project-module.admin.highlight')" outlined />
    <standard-toggle
      v-if="isProjectTOCEnabled"
      name="config.includeInTableOfContents"
      :label="t('project-module.admin.includeInTableOfContents')"
      outlined
    />
    <standard-toggle name="config.hasImage" :label="t('form.dialogs.add-image')" outlined />
    <div v-if="hasImageValue">
      <localized-select
        name="config.imageAlignment"
        label="Position"
        :editLocale="props.editLocale"
        :options="alignmentOptions"
        emit-value
        outlined
        map-options
      />
      <image-field
        alt-text-name="config.image.metadata.image.altText"
        version="v2"
        :cropping="cropping"
        :file="file!"
        :form-dirty="isDirty"
        :label="t('form.question.image.label')"
        :language-enabled="languageEnabled"
        @clear-image="handleClearImage"
        @save-image="handleSaveImage"
      />
      <localized-input
        name="config.imageCaption"
        :label="t('global.label.caption')"
        :editLocale="editLocale"
        outlined
        :language_enabled="props.languageEnabled"
      />
    </div>
  </div>
</template>
